import { ReactNode, useMemo } from "react";
import { Flex, Text } from "styles/utility-components";

import { Table } from "lib/core_components/Table";
import { commonTableColumns } from "lib/core_components/Table/ColumnTypes";
import { TypedColumnProps } from "lib/search/SearchTable";
import {
  measureColumns,
  rankingSortFromAntSorter,
  SpendRankingSort,
} from "../hooks/useRankingTable";
import { GroupedSpendTotal } from "../spendChartUtils";

type Props = {
  rows: GroupedSpendTotal[];
  sort: SpendRankingSort;
  onSortChange: (sort: SpendRankingSort) => void;
  isLoading: boolean;
  /** Rendered beside the title: the view's export button. */
  action: ReactNode;
};

const BUYER_TYPE_COLUMN: TypedColumnProps<GroupedSpendTotal> = {
  ...commonTableColumns.titleColumn,
  title: "Buyer type",
  key: "buyerType",
  render: (r) => r.name,
  sizeConfig: { large: 500, small: 200, medium: 400, xlarge: 600 },
};

/**
 * Every buyer type ranked by the measure being plotted. There are only a handful, so the
 * whole ranking fits on one page, and it is the same rows the chart draws.
 */
export function BuyerTypeRankingTable({ rows, sort, onSortChange, isLoading, action }: Props) {
  const columns = useMemo(() => [BUYER_TYPE_COLUMN, ...measureColumns(rows, sort)], [rows, sort]);

  return (
    <Flex column gap={16}>
      <Flex justifyContent="space-between" alignItems="center" gap={16}>
        <Text h3 color="sysTextSecondary">
          Rankings
        </Text>
        {action}
      </Flex>
      <Table<GroupedSpendTotal>
        ariaLabel="Spend buyer type ranking table"
        columns={columns}
        dataSource={rows}
        rowKey={(r) => r.key}
        loading={isLoading}
        pagination={false}
        onChange={(_pagination, _filters, sorter) => {
          const next = rankingSortFromAntSorter(sorter);
          if (next && (next.measure !== sort.measure || next.sortOrder !== sort.sortOrder)) {
            onSortChange(next);
          }
        }}
        isError={false}
      />
    </Flex>
  );
}
